/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CheckoutShippingFormContext } from "@insite/content-library/Pages/CheckoutShippingPage";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    paymentTerms = "paymentTerms",
    deliveryTerms = "deliveryTerms",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.paymentTerms]: "string";
        [fields.deliveryTerms]: "string";
    };
}

interface CheckoutPaymentAndDeliveryTermsStyles {
    errorMessageStyles?: TypographyPresentationProps;
}

const checkoutPaymentAndDeliveryTermsStyles: CheckoutPaymentAndDeliveryTermsStyles = {
    errorMessageStyles: {
        size: 12,
        weight: "normal",
        lineHeight: "22px",
        color: "danger.main",
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { paymentTerms, deliveryTerms },
    } = ownProps;
    const cmsProps = { paymentTerms, deliveryTerms };
    return {
        cmsProps,
    };
};

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = checkoutPaymentAndDeliveryTermsStyles;

const CheckoutPaymentAndDeliveryTerms: React.FC<Props> = ({ cmsProps }) => {
    const [termsAccepted, setTermsAccepted] = React.useState({
        paymentTerms: false,
        deliveryTerms: false,
    });
    const [error, setError] = React.useState("");
    const context = React.useContext(CheckoutShippingFormContext);

    React.useEffect(() => {
        context.validators["checkoutPaymentAndDeliveryTerms"] = validate;
    }, [termsAccepted]);

    const validate = async () => {
        const allTermsAccepted = Object.values(termsAccepted).every(o => o);
        await setError(allTermsAccepted ? "" : "You must agree to the payment and delivery terms before proceeding");
        return allTermsAccepted;
    };

    const handlePaymentTermsChange = (event: React.SyntheticEvent, value: boolean) => {
        setError("");
        setTermsAccepted(prevState => ({
            ...prevState,
            paymentTerms: value,
        }));
    };

    const handleDeliveryTermsChange = (event: React.SyntheticEvent, value: boolean) => {
        setError("");
        setTermsAccepted(prevState => ({
            ...prevState,
            deliveryTerms: value,
        }));
    };

    return (
        <>
            <StyledWrapper>
                <CheckboxGroup>
                    <Checkbox onChange={handlePaymentTermsChange} checked={termsAccepted.paymentTerms}>
                        {cmsProps.paymentTerms}
                    </Checkbox>
                    <Checkbox onChange={handleDeliveryTermsChange} checked={termsAccepted.deliveryTerms}>
                        {cmsProps.deliveryTerms}
                    </Checkbox>
                </CheckboxGroup>
                {error && <Typography {...styles.errorMessageStyles}>{error}</Typography>}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutPaymentAndDeliveryTerms),
    definition: {
        group: "Checkout" as any,
        displayName: "Checkout Payment & Delivery Terms",
        allowedContexts: ["CheckoutShippingPage", "CheckoutReviewAndSubmitPage"],
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.paymentTerms,
                displayName: "Payment Terms",
                editorTemplate: "TextField",
                defaultValue: "Payment Terms: 100% net 30 days",
                isRequired: true,
            },
            {
                fieldType: "General",
                name: fields.deliveryTerms,
                displayName: "Delivery Terms",
                editorTemplate: "TextField",
                defaultValue: "Delivery Terms: Carriage Paid To (CPT)",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
